/**
 * Defensive read of `meta.requestedWindow` from `/events/discover`.
 *
 * API-1814 adds `{ start, end }` instants (UTC ISO). The generated client
 * type in `@loci/types` may lag the response, and a pre-merge API omits the
 * field entirely. Callers must treat `null` as "use the local fallback" —
 * never assume the window is present.
 *
 * `meta.servedWindow` is the min/max event in the filtered set (null when
 * empty) and is intentionally not read here. The day strip follows the
 * requested filter, not the days that happened to have rows.
 */

export interface DiscoverRequestedWindow {
  readonly start: string;
  readonly end: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isInstant(value: unknown): value is string {
  return typeof value === 'string' && Number.isFinite(Date.parse(value));
}

/**
 * Return the requested window when both bounds are parseable instants and
 * `end` is not before `start`. Absent, null, or malformed → `null`.
 */
export function readDiscoverRequestedWindow(meta: unknown): DiscoverRequestedWindow | null {
  if (!isRecord(meta)) return null;
  const window = meta['requestedWindow'];
  if (!isRecord(window)) return null;
  const start = window['start'];
  const end = window['end'];
  if (!isInstant(start) || !isInstant(end)) return null;
  if (Date.parse(end) < Date.parse(start)) return null;
  return { start, end };
}
